import React, { useEffect, useRef } from 'react';
import { useSession } from '../context/SessionContext';
import { supabase } from '../services/supabaseClient';
import { NotificationService } from '../services/notificationService';
import { UserRole, JobStatus } from '../types';

export const NotificationManager: React.FC = () => {
    const { userProfile, role } = useSession();
    const notificationService = NotificationService.getInstance();
    const lastNotifiedJobId = useRef<string | null>(null);
    const initialized = useRef(false);

    // Initialize notification service once
    useEffect(() => {
        if (!userProfile?.id || !role || initialized.current) return;
        initialized.current = true;

        console.log('🔔 Initializing Notification Manager for', role);
        notificationService.initialize();
    }, [userProfile?.id, role]);

    // Listen to realtime job changes for notifications
    useEffect(() => {
        if (!userProfile?.id || !role) return;

        // 1. Listen to JOB changes via Supabase Realtime
        const jobsChannel = supabase
            .channel('public:jobs:notifications')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, async (payload) => {
                const newJob = payload.new as any;
                const oldJob = payload.old as any;
                const eventType = payload.eventType;

                if (!newJob) return;

                // --- WORKER LOGIC ---
                if (role === UserRole.WORKER) {
                    // A. Direct Job Request (INSERT)
                    if (eventType === 'INSERT' && newJob.worker_id === userProfile.id && newJob.status === 'PENDING') {
                        // Prevent duplicate
                        if (lastNotifiedJobId.current === newJob.id) return;
                        lastNotifiedJobId.current = newJob.id;

                        console.log('🔔 Notification: Direct Job Request');
                        await notificationService.showJobNotification({
                            jobId: newJob.id,
                            title: '📞 Direct Job Request',
                            customerName: newJob.customer_name || 'Customer',
                            skill: newJob.skill,
                            isBroadcast: false
                        });
                    }

                    // B. Job Cancelled (UPDATE)
                    if (eventType === 'UPDATE' && newJob.worker_id === userProfile.id && newJob.status === 'CANCELLED' && oldJob.status !== 'CANCELLED') {
                        await notificationService.showNotification('Job Cancelled', {
                            body: `Job for ${newJob.skill} has been cancelled.`,
                            tag: `job-cancelled-${newJob.id}`
                        });
                    }

                    // C. Broadcast Job (INSERT) - If no specific worker assigned
                    if (eventType === 'INSERT' && !newJob.worker_id && newJob.status === 'OPEN') {
                        await notificationService.showJobNotification({
                            jobId: newJob.id,
                            title: '🔔 New Job Available',
                            customerName: 'Nearby Customer',
                            skill: newJob.skill,
                            isBroadcast: true
                        });
                    }
                }

                // --- CUSTOMER LOGIC ---
                if (role === UserRole.CUSTOMER) {
                    // A. Job Accepted (UPDATE)
                    if (eventType === 'UPDATE' && newJob.customer_id === userProfile.id && newJob.status === 'ACCEPTED' && oldJob.status !== 'ACCEPTED') {
                        await notificationService.showNotification('Job Accepted! ✅', {
                            body: `${newJob.worker_name || 'Expert'} has accepted your job.`,
                            tag: `job-accepted-${newJob.id}`,
                            vibrate: [200, 100, 200]
                        });
                    }

                    // B. Job Started (UPDATE)
                    if (eventType === 'UPDATE' && newJob.customer_id === userProfile.id && newJob.status === 'IN_PROGRESS' && oldJob.status !== 'IN_PROGRESS') {
                        await notificationService.showNotification('Work Started 🛠️', {
                            body: `${newJob.worker_name || 'Expert'} has started working.`,
                            tag: `job-started-${newJob.id}`
                        });
                    }

                    // C. Job Completed (UPDATE)
                    if (eventType === 'UPDATE' && newJob.customer_id === userProfile.id && newJob.status === 'COMPLETED' && oldJob.status !== 'COMPLETED') {
                        await notificationService.showNotification('Work Completed 🎉', {
                            body: `Job finished! Please verify and pay ${newJob.worker_name || 'the expert'}.`,
                            tag: `job-complete-${newJob.id}`,
                            requireInteraction: true
                        });
                    }

                    // D. OTP Verified / Worker Arrived
                    if (eventType === 'UPDATE' && newJob.customer_id === userProfile.id && newJob.status === 'OTP_VERIFIED' && oldJob.status !== 'OTP_VERIFIED') {
                        await notificationService.showNotification('Worker Verified ✅', {
                            body: `You have successfully verified the expert.`,
                            tag: `job-otp-${newJob.id}`
                        });
                    }
                }
            })
            .subscribe();

        // 2. Listen to SYSTEM NOTIFICATIONS table
        const notificationsChannel = supabase
            .channel('public:notifications:system')
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications' }, async (payload) => {
                const notif = payload.new as any;

                // Filter by target group
                const isForMe =
                    notif.target_group === 'ALL' ||
                    (notif.target_group === 'WORKERS' && role === UserRole.WORKER) ||
                    (notif.target_group === 'CUSTOMERS' && role === UserRole.CUSTOMER) ||
                    (notif.user_id === userProfile.id);

                if (isForMe) {
                    await notificationService.showNotification(notif.title, {
                        body: notif.message,
                        tag: `system-${notif.id}`
                    });
                }
            })
            .subscribe();

        return () => {
            supabase.removeChannel(jobsChannel);
            supabase.removeChannel(notificationsChannel);
        };
    }, [userProfile?.id, role]);

    return null; // Logic only component
};
